import styled from 'styled-components';
import { Show } from '@elfsight/lib-utils';
import { Typography } from '@elfsight/widget-ui';
import { createReadableClass } from '@elfsight/widget-utils';
import type { TableLayout } from '../../../common';
import { useCheckoutColors } from '../providers';
import { useTableLayout } from '../utils';
import type { TableItemProps } from './table-item';
import { TableItem } from './table-item';

const COMPONENT_NAME = 'table';

export type DerivedTableItemProps = Pick<
  TableItemProps,
  'values' | 'label' | 'withInlineValues'
>;

export interface TableProps {
  title?: string;
  items: DerivedTableItemProps[];
  layout?: TableLayout;
}

export function Table({
  title,
  items,
  layout: derivedLayout = 'vertical'
}: TableProps) {
  const { textColor } = useCheckoutColors();
  const { tableLayout, tableContainerRef, setItemValueWidth } = useTableLayout({
    derivedLayout,
    itemsCount: items.length
  });

  const withTitle = !!title;

  return (
    <Container ref={tableContainerRef}>
      <Show when={withTitle}>
        <Typography
          textToken="text18Bold"
          color={textColor}
          className={createReadableClass({
            component: COMPONENT_NAME,
            element: 'title'
          })}
        >
          {title}
        </Typography>
      </Show>

      <TableSection>
        {items.map((itemProps, index) => (
          <TableItem
            key={index}
            {...itemProps}
            layout={tableLayout}
            handleValueResize={(newWidth: number) =>
              setItemValueWidth(index, newWidth)
            }
          />
        ))}
      </TableSection>
    </Container>
  );
}

const Container = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME
  })
})`
  display: flex;
  flex-direction: column;
  gap: 12px;

  width: 100%;
`;

const TableSection = styled.div.attrs({
  className: createReadableClass({
    component: COMPONENT_NAME,
    element: 'section'
  })
})`
  display: flex;
  flex-direction: column;
  gap: 12px;

  width: 100%;
`;
